<script setup lang="ts">
useSeoMeta({
  title: 'Contact — Shakir Abdulrahim',
  ogTitle: 'Contact — Shakir Abdulrahim',
  description: 'Get in touch with Shakir Abdulrahim — available for projects, collaborations, and opportunities.',
})

const { public: { recaptchaSiteKey } } = useRuntimeConfig()
const { data: settings } = await useFetch<Record<string, string>>('/api/settings')

const form = reactive({
  name: '',
  email: '',
  message: '',
})

const loading = ref(false)
const submitted = ref(false)
const error = ref('')

// Load reCAPTCHA v3 script
useHead({
  script: [
    { src: `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`, async: true },
  ],
})

function getRecaptchaToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(recaptchaSiteKey, { action: 'contact' })
        .then(resolve)
        .catch(reject)
    })
  })
}

async function handleSubmit() {
  loading.value = true
  error.value = ''
  try {
    const recaptchaToken = await getRecaptchaToken()
    await $fetch('/api/contact', {
      method: 'POST',
      body: { name: form.name, email: form.email, message: form.message, recaptchaToken },
    })
    submitted.value = true
  }
  catch (e: any) {
    error.value = e?.data?.statusMessage || 'Something went wrong. Please try again.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="py-24">
    <div class="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
      <p class="mb-4 text-sm font-medium tracking-widest text-[var(--color-amber)] uppercase">
        Contact
      </p>
      <h1 class="mb-4 text-4xl font-bold text-[var(--color-text)] sm:text-5xl">
        Get in Touch
      </h1>
      <p class="mb-2 text-lg text-[var(--color-text-muted)]">
        Have a project in mind or want to collaborate? Drop me a message.
      </p>
      <p class="mb-10 text-sm text-[var(--color-text-muted)]">
        <UIcon name="i-lucide-map-pin" class="mr-1 inline size-4" />
        Hail, Saudi Arabia
      </p>

      <div v-if="submitted" class="rounded-xl border border-[var(--color-amber)]/20 bg-[var(--color-amber)]/5 p-8 text-center">
        <UIcon name="i-lucide-check-circle" class="mx-auto mb-4 size-12 text-[var(--color-amber)]" />
        <h2 class="text-xl font-semibold text-[var(--color-text)]">
          Message sent!
        </h2>
        <p class="mt-2 text-[var(--color-text-muted)]">
          I'll get back to you as soon as possible.
        </p>
      </div>

      <div v-if="error" class="mb-6 rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-400">
        {{ error }}
      </div>

      <form v-if="!submitted" class="space-y-6" @submit.prevent="handleSubmit">
        <UFormField label="Name" class="w-full">
          <UInput
            v-model="form.name"
            placeholder="Your name"
            required
            size="lg"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Email" class="w-full">
          <UInput
            v-model="form.email"
            type="email"
            placeholder="your@email.com"
            required
            size="lg"
            class="w-full"
          />
        </UFormField>

        <UFormField label="Message" class="w-full">
          <UTextarea
            v-model="form.message"
            placeholder="Tell me about your project..."
            required
            :rows="5"
            size="lg"
            class="w-full"
          />
        </UFormField>

        <UButton
          type="submit"
          size="lg"
          :loading="loading"
          class="bg-[var(--color-amber)] text-[var(--color-dark-base)] hover:bg-[var(--color-amber)]/90"
        >
          Send Message
        </UButton>
        <p class="mt-3 text-xs text-[var(--color-text-muted)]">
          This site is protected by reCAPTCHA and the Google
          <a href="https://policies.google.com/privacy" target="_blank" class="underline">Privacy Policy</a> and
          <a href="https://policies.google.com/terms" target="_blank" class="underline">Terms of Service</a> apply.
        </p>
      </form>

      <div class="mt-12 flex items-center gap-6">
        <a
          v-if="settings?.github_url"
          :href="settings.github_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2 text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-amber)]"
        >
          <UIcon name="i-simple-icons-github" class="size-5" />
          GitHub
        </a>
        <a
          v-if="settings?.twitter_url"
          :href="settings.twitter_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2 text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-amber)]"
        >
          <UIcon name="i-simple-icons-x" class="size-5" />
          X
        </a>
        <a
          v-if="settings?.email"
          :href="`mailto:${settings.email}`"
          class="flex items-center gap-2 text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-amber)]"
        >
          <UIcon name="i-lucide-mail" class="size-5" />
          Email
        </a>
      </div>
    </div>
  </div>
</template>
